/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PageviewIcon from '@material-ui/icons/Pageview';
import { WebFileSystemAccess } from '../../lib/filesystem';

import { Button } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import PublishIcon from '@material-ui/icons/Publish';
import { WbInfoTooltip } from '@agilelab/plugin-wb-platform';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { useNavigate } from 'react-router';
import { skeletonRenderingPreviewRouteRef } from '../../routes';

const useStyles = makeStyles(theme => ({
  introText: {
    textAlign: 'center',
    marginTop: theme.spacing(2),
    fontWeight: 700,
  },
  card: {
    position: 'relative',
    maxWidth: 430,
    height: 300,
    marginTop: theme.spacing(4),
    margin: theme.spacing(0, 2),
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '16px',
    flex: 1,
  },
  infoIcon: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  actionButton: {
    width: '126px',
  },
}));

interface EditorIntroProps {
  style?: JSX.IntrinsicElements['div']['style'];
  onSelect?: (option: 'local' | 'form') => void;
}

export function TemplateEditorIntro(props: EditorIntroProps) {
  const classes = useStyles();
  const supportsLoad = WebFileSystemAccess.isSupported();
  const theme = useTheme();
  const navigate = useNavigate();
  const skeletonPreviewLink = useRouteRef(skeletonRenderingPreviewRouteRef);

  const cardLoadLocal = (
    <Card className={classes.card} elevation={4}>
      <CardContent className={classes.cardContent}>
        <Typography
          variant="h6"
          gutterBottom
          color={supportsLoad ? undefined : 'textSecondary'}
          style={{ display: 'flex', flexFlow: 'row nowrap' }}
        >
          Local Directory Template Studio
        </Typography>
        <PublishIcon
          htmlColor={theme.palette.primary.light}
          style={{ fontSize: '96px' }}
        />
        <Typography
          variant="body2"
          color={supportsLoad ? undefined : 'textSecondary'}
        >
          Load a local template directory from your computer, allowing you to
          edit and generate a preview of an entity instantiated from this
          template.
        </Typography>

        <Button
          className={classes.actionButton}
          disabled={!supportsLoad}
          color="primary"
          variant="contained"
          onClick={() => props.onSelect?.('local')}
        >
          Load
        </Button>
      </CardContent>
      {!supportsLoad && (
        <div className={classes.infoIcon}>
          <WbInfoTooltip
            placement="top"
            title="Only supported in some Chromium-based browsers"
            icon={<InfoOutlinedIcon />}
          />
        </div>
      )}
    </Card>
  );

  const cardFormEditor = (
    <Card className={classes.card} elevation={4}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h6" gutterBottom>
          Edit Registered Template
        </Typography>
        <EditIcon
          htmlColor={theme.palette.primary.light}
          style={{ fontSize: '96px' }}
        />
        <Typography variant="body2">
          Preview and edit a template form by selecting one from the existing
          templates. This tool is intended only for reviewing template changes
          within the wizard and does not support previewing an instantiated
          entity.
        </Typography>
        <Button
          className={classes.actionButton}
          color="primary"
          variant="contained"
          onClick={() => props.onSelect?.('form')}
        >
          Edit
        </Button>
      </CardContent>
    </Card>
  );

  const cardCatalogInfoPreview = (
    <Card className={classes.card} elevation={4}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h6" gutterBottom>
          Catalog Info Rendering Preview
        </Typography>
        <PageviewIcon
          htmlColor={theme.palette.primary.light}
          style={{ fontSize: '96px' }}
        />
        <Typography variant="body2">
          Preview how a catalog-info.yaml will be rendered once it is
          instantiated from a template by providing the parameters.yaml output
          file without the need to run the full template flow.
        </Typography>
        <Button
          className={classes.actionButton}
          color="primary"
          variant="contained"
          onClick={() => navigate(skeletonPreviewLink())}
        >
          Open
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div style={props.style}>
      <Typography variant="body1" className={classes.introText}>
        Get started by choosing one of the options below
      </Typography>
      <div
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          alignItems: 'stretch',
          justifyContent: 'center',
          alignContent: 'flex-start',
        }}
      >
        {supportsLoad && cardLoadLocal}
        {cardFormEditor}
        {cardCatalogInfoPreview}
        {!supportsLoad && cardLoadLocal}
      </div>
    </div>
  );
}
